import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PageLoadingState } from '../../../../components/LoadingSpinner';
import { AppSidebar } from '../../../../components/AppSidebar';
import { AppTopBar } from '../../../../components/AppTopBar';
import { fetchDocumentLayoutTemplate } from '../../utils/documentLayoutApi';
import type { DocumentLayoutTemplateRow } from '../../utils/documentLayoutTypes';
import { SETTINGS_PATH_INVOICE_TEMPLATE } from '../../../../utils/settingsRoutes';
import { DocumentLayoutEditor } from './DocumentLayoutEditor';

function withInstance(path: string): string {
  const match = window.location.pathname.match(/^\/i\/[^/]+/);
  const prefix = match ? match[0] : '/i/default';
  return `${prefix}${path.startsWith('/') ? path : `/${path}`}`;
}

export function DocumentLayoutEditorPage() {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const [template, setTemplate] = React.useState<DocumentLayoutTemplateRow | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    if (!templateId) {
      setError('Vorlage nicht gefunden.');
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchDocumentLayoutTemplate(templateId)
      .then((tpl) => {
        if (cancelled) return;
        if (!tpl) {
          setError('Vorlage nicht gefunden.');
          setTemplate(null);
        } else {
          setTemplate(tpl);
          setError(null);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  return (
    <div className="flex h-screen min-h-0 overflow-hidden bg-app-bg font-sans">
      <AppSidebar active="settings" />
      <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden pb-16 md:pb-0">
        <AppTopBar
          title="Rechnungsvorlage"
          leftAccessory={
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted"
              onClick={() => navigate(withInstance(SETTINGS_PATH_INVOICE_TEMPLATE))}
              title="Zurück zur Übersicht"
              aria-label="Zurück zur Übersicht"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          }
        />
        {loading ? (
          <PageLoadingState />
        ) : error || !template ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6">
            <p className="text-sm text-red-600">{error || 'Vorlage nicht gefunden.'}</p>
            <button
              type="button"
              className="inline-flex h-9 items-center rounded-md border border-app-border bg-app-surface px-3.5 text-sm font-medium text-app-text-muted hover:bg-app-hover"
              onClick={() => navigate(withInstance(SETTINGS_PATH_INVOICE_TEMPLATE))}
            >
              Zurück zur Übersicht
            </button>
          </div>
        ) : (
          <DocumentLayoutEditor
            template={template}
            onSaved={setTemplate}
            onDeleted={() => navigate(withInstance(SETTINGS_PATH_INVOICE_TEMPLATE))}
          />
        )}
      </main>
    </div>
  );
}
